// src/App.jsx
import React, { useRef, useEffect, useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import QuienesSomos from "./components/QuienesSomos";
import Servicios from "./components/Servicios";
import Clientes from "./components/Clientes";
import Alianzas from "./components/Alianzas";
import Resenas from "./components/Resenas";
import Contacto from "./components/Contacto";

// Página de detalle de servicios (data-driven, ver src/data/servicios.js)
import ServicioDetalle from "./pages/ServicioDetalle";

import { ScrollContext } from "./context/ScrollContext";

function ScrollHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { scrollToSection, refs } = useContext(ScrollContext);

  useEffect(() => {
    if (location.state?.scrollTo) {
      const target = location.state.scrollTo;

      const scrollIfExists = () => {
        if (target === "top" && refs.topRef?.current) {
          scrollToSection(refs.topRef);
          return true;
        } else if (refs[target]?.current) {
          scrollToSection(refs[target]);
          return true;
        }
        return false;
      };

      if (!scrollIfExists()) {
        // Esperar hasta que la sección exista
        const interval = setInterval(() => {
          if (scrollIfExists()) {
            clearInterval(interval);
          }
        }, 50);
      }

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, refs, scrollToSection, navigate]);

  return null;
}

function App() {
  const topRef = useRef(null);
  const quienesRef = useRef(null);
  const serviciosRef = useRef(null);
  const alianzasRef = useRef(null);
  const clientesRef = useRef(null);
  const resenasRef = useRef(null);
  const contactoRef = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <ScrollContext.Provider
      value={{
        refs: { topRef, quienesRef, serviciosRef, alianzasRef, clientesRef, resenasRef, contactoRef },
        scrollToSection,
      }}
    >
      <Router>
        <Navbar />
        <ScrollHandler />

        <Routes>
          <Route
            path="/"
            element={
              <div ref={topRef}>
                <Hero contactoRef={contactoRef} />

                <motion.div className="scroll-mt-24" initial="hidden" animate="visible" variants={sectionVariants} transition={{ duration: 0.8 }} ref={quienesRef}>
                  <QuienesSomos />
                </motion.div>

                <motion.div className="scroll-mt-24" initial="hidden" animate="visible" variants={sectionVariants} transition={{ duration: 0.8 }} ref={serviciosRef}>
                  <Servicios />
                </motion.div>

                <motion.div className="scroll-mt-24" initial="hidden" animate="visible" variants={sectionVariants} transition={{ duration: 0.8 }} ref={alianzasRef}>
                  <Alianzas />
                </motion.div>

                <motion.div className="scroll-mt-24" initial="hidden" animate="visible" variants={sectionVariants} transition={{ duration: 0.8 }} ref={clientesRef}>
                  <Clientes />
                </motion.div>

                <motion.div className="scroll-mt-24" initial="hidden" animate="visible" variants={sectionVariants} transition={{ duration: 0.8 }} ref={resenasRef}>
                  <Resenas />
                </motion.div>

                <motion.div className="scroll-mt-24" initial="hidden" animate="visible" variants={sectionVariants} transition={{ duration: 0.8 }} ref={contactoRef}>
                  <Contacto />
                </motion.div>
              </div>
            }
          />

          {/* Ruta de servicios (contenido en src/data/servicios.js) */}
          <Route path="/servicio/:slug" element={<ServicioDetalle />} />

          {/* Cualquier URL desconocida vuelve al inicio */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Botón flotante WhatsApp */}
        <a
          href="https://wa.me/5493517144295"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Contactar por WhatsApp"
          className="fixed bottom-6 right-6 bg-green-500 p-4 rounded-full shadow-lg hover:scale-110 transition-transform duration-300 animate-pulse"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 32 32"
            fill="white"
            aria-hidden="true"
            className="w-7 h-7"
          >
            <path d="M16 .395C7.16.395.004 7.55.004 16.395c0 2.882.754 5.686 2.184 8.156L.004 32l7.707-2.184a15.9 15.9 0 0 0 8.289 2.279c8.84 0 16-7.155 16-16S24.84.395 16 .395zm0 29.01c-2.68 0-5.289-.715-7.563-2.07l-.539-.32-4.563 1.293 1.301-4.45-.352-.574A13.4 13.4 0 0 1 2.59 16.395c0-7.42 6-13.43 13.41-13.43s13.41 6.01 13.41 13.43-6 13.43-13.41 13.43zm7.367-9.6c-.402-.2-2.379-1.18-2.75-1.313-.371-.137-.641-.2-.91.201s-1.039 1.313-1.27 1.582c-.23.27-.469.304-.871.102-.398-.2-1.68-.619-3.2-1.977-1.184-1.055-1.984-2.36-2.215-2.758-.23-.398-.023-.613.176-.813.18-.18.398-.47.602-.707.199-.23.27-.398.402-.668.137-.27.07-.5-.035-.707-.102-.2-.91-2.2-1.246-3.02-.328-.797-.664-.691-.91-.707-.23-.02-.5-.02-.77-.02s-.707.102-1.078.5c-.371.398-1.414 1.383-1.414 3.363s1.449 3.902 1.652 4.172c.199.27 2.84 4.34 6.887 6.082.961.414 1.711.66 2.293.844.961.305 1.84.262 2.535.16.773-.117 2.379-.973 2.715-1.91.336-.938.336-1.742.234-1.91-.098-.16-.363-.27-.766-.465z" />
          </svg>
        </a>
      </Router>
    </ScrollContext.Provider>
  );
}

export default App;
